import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Ruler, Factory, Volume2, VolumeX } from 'lucide-react';
import { PLANT_INFO } from '../mock/mock';

const OurPlant = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div>
      {/* Hero */}
      <section className="pt-24 pb-16 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">
            Manufacturing &middot; Our Plant
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            From timber preparation to <span className="italic text-[#a67c3d]">finished furniture</span>, across two Jaipur units.
          </h1>
          <p className="mt-8 max-w-3xl text-lg text-[#4a3f36] leading-relaxed">
            {PLANT_INFO.intro}
          </p>
        </div>
      </section>

      {/* 360° Plant Tour Video */}
      <section className="pt-10 md:pt-14 pb-10 md:pb-14 bg-[#faf6f0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
            <div>
              <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">
                360&deg; Plant Overview
              </p>
              <h2 className="font-serif text-2xl md:text-4xl text-[#1a1614] leading-tight">
                A walk through <span className="italic text-[#a67c3d]">every department.</span>
              </h2>
            </div>
            <p className="text-sm text-[#6b5847] max-w-md md:text-right">
              Seasoning &middot; joinery &middot; upholstery &middot; finishing &mdash; captured in a single continuous take.
            </p>
          </div>

          <div
            className="relative w-full overflow-hidden bg-[#1a1614] shadow-2xl"
            style={{ aspectRatio: '16 / 9' }}
            data-testid="plant-tour-video"
          >
            <video
              ref={videoRef}
              src={PLANT_INFO.tourVideo}
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ imageRendering: 'auto' }}
            />
            {/* Subtle vignette so the play area feels cinematic */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#1a1614]/40 via-transparent to-transparent" />

            {/* Overlay chip */}
            <div className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 bg-[#faf6f0]/90 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase text-[#1a1614]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a67c3d] animate-pulse" />
              Live from the atelier
            </div>

            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              aria-label={muted ? 'Unmute plant tour' : 'Mute plant tour'}
              data-testid="plant-video-mute"
              className="absolute bottom-4 right-4 md:bottom-6 md:right-6 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#faf6f0]/15 hover:bg-[#faf6f0]/30 backdrop-blur text-[#faf6f0] border border-[#faf6f0]/30 transition-colors"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>

            {/* Caption bar */}
            <div className="absolute inset-x-0 bottom-0 px-5 md:px-8 pb-4 md:pb-6 pt-16 bg-gradient-to-t from-[#1a1614]/85 via-[#1a1614]/25 to-transparent">
              <p className="text-[10px] md:text-xs tracking-[0.28em] uppercase text-[#d4b483] mb-1">
                Department Works &middot; Jaitpura Unit
              </p>
              <h3 className="font-serif text-lg md:text-2xl text-[#faf6f0]">
                60,000 sq. ft. of joinery, upholstery and finishing.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities strip */}
      <section className="py-14 md:py-20 bg-[#1a1614] text-[#faf6f0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4 text-[#d4b483]">
              <MapPin size={18} />
              <p className="text-[10px] tracking-[0.32em] uppercase">Locations</p>
            </div>
            <div className="space-y-4">
              {PLANT_INFO.locations.map(l => (
                <div key={l.name}>
                  <p className="font-serif text-2xl md:text-3xl">{l.name}</p>
                  <p className="text-sm text-[#c9bfae]">{l.region}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4 text-[#d4b483]">
              <Ruler size={18} />
              <p className="text-[10px] tracking-[0.32em] uppercase">Operational Floor Area</p>
            </div>
            <p className="font-serif text-4xl md:text-5xl text-[#faf6f0]">{PLANT_INFO.floorArea}</p>
            <p className="text-sm text-[#c9bfae] mt-3">
              Combined across both manufacturing units &mdash; one of the largest integrated timber-processing and furniture-making set-ups in the region.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4 text-[#d4b483]">
              <Factory size={18} />
              <p className="text-[10px] tracking-[0.32em] uppercase">Set-up</p>
            </div>
            <p className="font-serif text-2xl md:text-3xl">Pro-Tech</p>
            <p className="text-sm text-[#c9bfae] mt-3">
              Advanced Pro-Tech equipment and connected departments support control from timber preparation to final packing, enabling the precision and consistency required for project manufacturing.
            </p>
          </div>
        </div>
      </section>

      {/* Machinery grid */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-3">Equipment</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1a1614] leading-[1.05]">
                The machines behind <span className="italic text-[#a67c3d]">every finish.</span>
              </h2>
            </div>
            <p className="text-sm text-[#6b5847] max-w-md">
              A short list of the specialist machinery that runs on our shop floor day and night.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {PLANT_INFO.machinery.map((m, i) => (
              <div
                key={m.name}
                data-testid={`plant-machine-${i}`}
                className="group relative overflow-hidden bg-[#faf6f0] border border-[#e0d6c5] hover:border-[#a67c3d] hover:shadow-xl transition-all duration-500"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1614]">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[900ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/70 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute top-3 left-3 bg-[#faf6f0]/90 backdrop-blur px-2.5 py-1 font-serif text-[10px] tracking-[0.24em] uppercase text-[#a67c3d]">
                    0{(i + 1).toString().padStart(2, '0').slice(-2)}
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <p className="font-serif text-base md:text-lg text-[#1a1614] leading-snug">{m.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#f2ebde]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] mb-6 leading-tight">
            See the work where <span className="italic text-[#a67c3d]">it happens</span>.
          </h2>
          <p className="text-[#4a3f36] max-w-2xl mx-auto mb-8 md:mb-10 text-base md:text-lg">
            RCD welcomes designers, architects and hospitality buyers to its Jaitpura and Bassi units to see materials and samples in process, explore the departments and meet the people responsible for bringing each project into production.
          </p>
          <Link
            to="/contact"
            data-testid="plant-visit-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
          >
            Book a Plant Visit <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurPlant;
